/* ============
 * Campaigns Actions
 * ============
 *
 * The actions available for the campaigns module.
 */

import downloadjs from 'downloadjs';
import store from '@/store';
import i18n from '@/i18n';
import router from '@/router';
import { campaignsApi } from '@/api/CampaignsApi';
import * as types from './types';


/**
 * Action fired when all campaigns will be fetched.
 *
 * @param {function} commit Commit function to update the store.
 * @param {function} fn     Callback to edit the parameters on the api.
 */
const all = ({ commit }, fn = null) => {
  if (typeof fn === 'function') {
    fn(campaignsApi);
  }
  return campaignsApi.all()
    .then((response) => {
      const searchParam = campaignsApi.getSearchParam();
      let newTotalPage = Math.ceil(response.data.count / searchParam.limit);
      if (newTotalPage === 0) {
        newTotalPage = 1;
      }
      const data = {
        campaigns: response.data.dispatchs,
        pagination: {
          limit: searchParam.limit,
          totalCount: response.data.count,
          totalPages: newTotalPage,
        },
      };
      commit(types.ALL, data);
      return data;
    })
    .catch(() => {
      commit(types.NONE);
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaigns.messages.all.error'),
      });
    });
};

/**
 * Action fired when a campaign will be created.
 *
 * @param {function} commit  Commit function to update the store.
 * @param {Object}   campaign   The campaign that will be created.
 */
const create = ({ commit }, params) => campaignsApi
  .create(
    params.campaign,
    params.siret,
    params.recipientsAuto,
    params.review,
    params.noDistribution,
  ).then(data => data)
  .catch((error) => {
    console.log(error);
    store.dispatch('application/openSnackbar', {
      message: i18n.t('campaigns.messages.create.error'),
    });
    return Promise.reject('error');
  });


/**
 * Action fired when an campaign will be updated.
 *
 * @param {function} commit  Commit function to update the store.
 * @param {Object}   campaign   The campaign that will be updated.
 */
const update = ({ commit }, campaign) => {
  store.dispatch('application/startLoading');
  campaignsApi.update(campaign.id, campaign)
    .then(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: `<span data-id="${campaign.id}">${i18n.t('campaign.update.success')}</span>`,
      });

      router.push({
        name: 'campaigns.show',
      });
    })
    .catch(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.update.failure'),
      });
    });
};

/**
 * Action fired when an campaign will be destroyed.
 *
 * @param {function} commit    Commit function to update the store.
 * @param {Object}   campaignId   The campaign that will be destroyed.
 */
const destroyDispatchedDocs = ({ commit }, dispatchedDocIds) => {
  store.dispatch('application/startLoading');
  campaignsApi.destroyDispatchedDocs(dispatchedDocIds)
    .then(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.delete.couple.success'),
      });
    })
    .catch(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.delete.couple.failure'),
      });
    });
};

const getDispatchedDocs = ({ commit }, fn = null) => {
  if (typeof fn === 'function') {
    fn(campaignsApi);
  }
  return campaignsApi.getDispatchedDocs()
    .then(data => data.data)
    .catch(() => {
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.get.dispatchedDocs.failure'),
      });
    });
};

const retryDispatch = ({ commit }, id) => {
  router.push('/campaigns');
  store.dispatch('application/openSnackbar', {
    message: i18n.t('campaign.retry.in.progress'),
  });
  return campaignsApi.retryDispatch(id);
};

const sendDispatch = ({ commit }, id) => {
  router.push('/campaigns');
  store.dispatch('application/openSnackbar', {
    message: i18n.t('campaign.validate.in.progress'),
  });
  return campaignsApi.sendDispatch(id);
};

const sendNeedDispatchingDispatch = ({ commit }, id) => {
  router.push('/campaigns');
  store.dispatch('application/openSnackbar', {
    message: i18n.t('campaign.validate.in.progress'),
  });
  return campaignsApi.sendNeedDispatchingDispatch(id);
};


const retryDispatchedDoc = ({ commit }, id) => {
  store.dispatch('application/startLoading');
  return campaignsApi.retryDispatchedDoc(id)
    .then((data) => {
      store.dispatch('application/finishLoading');
      return data;
    })
    .catch(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.dispactchedDoc.retry.failure'),
      });
    });
};

const sendDispatchedDoc = ({ commit }, id) => {
  store.dispatch('application/startLoading');
  return campaignsApi.sendDispatchedDoc(id)
    .then((data) => {
      store.dispatch('application/finishLoading');
      return data;
    })
    .catch(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.dispactchedDoc.send.failure'),
      });
    });
};

const downloadFailedDocs = ({ commit }, params) => {
  store.dispatch('application/startLoading');
  return campaignsApi.downloadFailedDocs(params)
    .then((data) => {
      downloadjs(data, 'documents.pdf');
      store.dispatch('application/finishLoading');
    })
    .catch(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.failedDoc.get.failure'),
      });
    });
};

const downloadIgnoredDocs = ({ commit }, id) => {
  store.dispatch('application/startLoading');
  return campaignsApi.downloadIgnoredDocs(id)
    .then((data) => {
      downloadjs(data, 'documents.zip');
      store.dispatch('application/finishLoading');
    })
    .catch(() => {
      store.dispatch('application/finishLoading');
      store.dispatch('application/openSnackbar', {
        message: i18n.t('campaign.failedDoc.get.failure'),
      });
    });
};

const changeLimit = ({ commit }, limit) => {
  commit(types.UPDATE_LIMIT, limit);
};

const changeCurrentPage = ({ commit }, currentPageNumber) => {
  commit(types.UPDATE_CURRENT_PAGE, currentPageNumber);
};

export default {
  all,
  changeCurrentPage,
  changeLimit,
  create,
  destroyDispatchedDocs,
  downloadFailedDocs,
  downloadIgnoredDocs,
  getDispatchedDocs,
  retryDispatch,
  retryDispatchedDoc,
  sendDispatch,
  sendNeedDispatchingDispatch,
  sendDispatchedDoc,
  update,
};



// WEBPACK FOOTER //
// ./src/store/modules/campaigns/actions.js